import React, { FC, useCallback, useState } from "react";
import { Button, ButtonProps, Dialog, IconButton, Input, Tooltip, URLInput } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface Free2GrowConnectionProps extends IntegrationConnectionProps {
    integration: "free2grow";
    endpoint?: string;
    service?: string;
}

export interface Free2GrowIntegrationCardProps extends IntegrationCardProp {
    /**
     * The lead-source id minted when this business connected. Shown so they can pass it to their
     * Free-2-Grow rep, who registers it against their tenant in Archer.
     */
    referenceId?: string;
    endpoint?: string;
    service?: string;

    onConnect?(props: Free2GrowConnectionProps): void;
}

/**
 * Free-2-Grow (Archer) setup.
 *
 * Deliberately no required fields. The partner key is one XAPP-wide secret held in SSM and read at
 * send time, and one endpoint serves every business, so connecting is a single click. The endpoint
 * and service overrides are here for a test account and should stay empty otherwise -- sending an
 * override that matches the default would pin a copy of it to this business.
 */
export const Free2GrowIntegrationCard: FC<Free2GrowIntegrationCardProps> = (props) => {

    const { checked, disabled, isLinked, isActive, onConnect, onDisconnect } = props;

    const [endpoint, setEndpoint] = useState<string>(props.endpoint || "");
    const [service, setService] = useState<string>(props.service || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showDisconnect, setShowDisconnect] = useState<boolean>(false);
    const [showDetails, setShowDetails] = useState<boolean>(false);

    const resetForm = useCallback(() => {
        setEndpoint(props.endpoint || "");
        setService(props.service || "");
    }, [props.endpoint, props.service]);

    const handleCloseDialog = useCallback(() => {
        resetForm();
        setShowDialog(false);
    }, [resetForm]);

    const handleFree2GrowClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            setShowDialog(true);
        }
    }, [checked, props.onClick]);

    const handleConnectFree2Grow = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "free2grow",
                endpoint: endpoint || undefined,
                service: service || undefined
            });
            setShowDialog(false);
        }
    }, [onConnect, endpoint, service]);

    const handleCopyReferenceId = useCallback(() => {
        if (props.referenceId) {
            navigator.clipboard.writeText(props.referenceId);
        }
    }, [props.referenceId]);

    // Disconnecting retires the id their rep registered, and reconnecting mints a new one that has
    // to be registered again -- so it asks first rather than acting on the card's close button.
    const handleDisconnectRequested = useCallback(() => {
        setShowDisconnect(true);
    }, []);

    const handleConfirmDisconnect = useCallback(() => {
        setShowDisconnect(false);
        if (typeof onDisconnect === "function") {
            onDisconnect();
        }
    }, [onDisconnect]);

    const disconnectActions: ButtonProps[] = [
        {
            label: "Keep Connected",
            variation: "secondary",
            onClick: () => setShowDisconnect(false)
        },
        {
            label: "Disconnect",
            variation: "primary",
            onClick: handleConfirmDisconnect
        }
    ];

    const isValidEndpoint = useCallback((url: string): boolean => {
        if (!url) {
            // Empty is valid -- it means "use the production endpoint".
            return true;
        }
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    }, []);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: handleCloseDialog
        }
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            variation: "primary",
            disabled: !isValidEndpoint(endpoint),
            onClick: handleConnectFree2Grow
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/free-2-grow/free-2-grow-logo.png"
                text="Free-2-Grow"
                href={() => "/integrations/free2grow"}
                onClick={handleFree2GrowClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectRequested : undefined}
            />
            {isLinked && props.referenceId && (
                <div style={{ margin: "8px 0 16px" }}>
                    <Button variation="secondary" onClick={() => setShowDetails(true)}>
                        Integration Details
                    </Button>
                </div>
            )}
            <Dialog
                active={showDetails}
                actions={[{ label: "Close", variation: "secondary", onClick: () => setShowDetails(false) }]}
                title="Free-2-Grow Details"
                onClose={() => setShowDetails(false)}
            >
                <p>
                    Free-2-Grow routes this business's leads using the Lead Source ID below. Give it
                    to your Free-2-Grow rep so they can register XAPP as a lead source.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <code style={{ userSelect: "all" }}>{props.referenceId}</code>
                    <Tooltip content="Copy">
                        <IconButton iconName="content_copy" color="primary" onClick={handleCopyReferenceId} />
                    </Tooltip>
                </div>
            </Dialog>
            <Dialog
                active={showDisconnect}
                actions={disconnectActions}
                title="Disconnect Free-2-Grow?"
                onClose={() => setShowDisconnect(false)}
            >
                <p>
                    Free-2-Grow routes this business's leads using its Lead Source ID. Disconnecting
                    retires that ID.
                </p>
                <p>
                    Reconnecting later issues a <strong>new</strong> ID, which has to be registered
                    with their Free-2-Grow rep again before leads reach them.
                </p>
            </Dialog>
            <Dialog
                active={showDialog}
                actions={actions}
                title="Free-2-Grow Setup"
                onClose={handleCloseDialog}
            >
                <p>Leads are sent to Free-2-Grow's Archer platform for call and text outreach.</p>
                <p>
                    Archer must enrol this business before leads will be actioned -- until then they
                    are accepted and held.
                </p>
                <URLInput label="Endpoint URL (Optional)" value={endpoint} onChange={(val) => setEndpoint(String(val))} />
                <Input label="Service (Optional)" value={service} onChange={(val) => setService(String(val))} />
            </Dialog>
        </>
    );
}
